import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useInstance } from '../hooks/useInstance';
import { apiClient } from '../services/api';
import { LogOut, User, Lock, MessageSquare, LayoutDashboard, FolderTree, Building2, AlertCircle, RefreshCw, ShieldCheck, Download, HelpCircle, Settings, X } from 'lucide-react';
import { UpdatePassword } from './UpdatePassword';
import { FeedbackModal } from './FeedbackModal';
import { InstanceSelector } from './InstanceSelector';


const BETA_BANNER_KEY = 'vocatree_beta_banner_dismissed';
// Module-level flag so dismissal survives component remounts within the same session
let betaBannerDismissedInSession = localStorage.getItem(BETA_BANNER_KEY) === 'true';

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { setupError, clearSetupError, refreshInstances, selectedInstanceId } = useInstance();
  const [betaBannerDismissed, setBetaBannerDismissed] = useState(betaBannerDismissedInSession);

  const dismissBetaBanner = () => {
    localStorage.setItem(BETA_BANNER_KEY, 'true');
    betaBannerDismissedInSession = true;
    setBetaBannerDismissed(true);
  };

  const handleLibraryHover = useCallback(() => {
    apiClient.prefetchCategoriesWithCards(selectedInstanceId ?? undefined);
  }, [selectedInstanceId]);
  const location = useLocation();
  const navigate = useNavigate();
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
    setShowUserMenu(false);
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  const handleUserMenuClick = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleUpdatePassword = () => {
    setShowUserMenu(false);
    setShowUpdatePassword(true);
  };

  const handleSendFeedback = () => {
    setShowUserMenu(false);
    setShowFeedback(true);
  };

  const handleSignOut = () => {
    setShowUserMenu(false);
    logout();
    navigate('/login', { replace: true });
  };

  // Get user initials from email for placeholder
  const getUserInitials = (email: string | undefined): string => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email[0].toUpperCase();
  };

  // Get background color based on email (for consistent color per user)
  const getAvatarColor = (email: string | undefined): string => {
    if (!email) return 'bg-blue-500';
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ];
    const index = email.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3 sm:space-x-6 flex-1 min-w-0">
              <a href="/dashboard" className="flex items-center gap-2 min-w-0 flex-shrink-0">
                <img src="/icon.svg" alt="VocaBranch" className="h-8 w-8" />
                <span className="hidden sm:block text-base font-bold text-gray-900">VocaBranch</span>
              </a>
              
              {/* Navigation - Desktop */}
              <nav className="hidden md:flex items-center space-x-1">
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/dashboard'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/tree"
                  onMouseEnter={handleLibraryHover}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/tree'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FolderTree className="h-4 w-4" />
                  <span>Library</span>
                </Link>
                <Link
                  to="/collections"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/collections'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Building2 className="h-4 w-4" />
                  <span>Collections</span>
                </Link>
                {user?.is_admin && (
                  <Link
                    to="/admin/users"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-purple-100 text-purple-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    <span>Admin</span>
                  </Link>
                )}
              </nav>

              {/* Navigation - Mobile */}
              <nav className="md:hidden flex items-center space-x-1 ml-auto mr-2">
                <Link
                  to="/dashboard"
                  className={`p-2 rounded-md transition-colors ${
                    location.pathname === '/dashboard'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Dashboard"
                  aria-label="Dashboard"
                >
                  <LayoutDashboard className="h-5 w-5" />
                </Link>
                <Link
                  to="/tree"
                  onMouseEnter={handleLibraryHover}
                  className={`p-2 rounded-md transition-colors ${
                    location.pathname === '/tree'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Library"
                  aria-label="Library"
                >
                  <FolderTree className="h-5 w-5" />
                </Link>
                <Link
                  to="/collections"
                  className={`p-2 rounded-md transition-colors ${
                    location.pathname === '/collections'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Collections"
                  aria-label="Collections"
                >
                  <Building2 className="h-5 w-5" />
                </Link>
                {user?.is_admin && (
                  <Link
                    to="/admin/users"
                    className={`p-2 rounded-md transition-colors ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-purple-100 text-purple-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Admin"
                    aria-label="Admin"
                  >
                    <ShieldCheck className="h-5 w-5" />
                  </Link>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <InstanceSelector />
              <div className="relative">
                <button
                  ref={buttonRef}
                  onClick={handleUserMenuClick}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:ring-2 hover:ring-gray-300 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="User menu"
                  aria-expanded={showUserMenu}
                  aria-haspopup="menu"
                >
                  {/* Profile Photo Placeholder */}
                  <div className={`w-10 h-10 rounded-full ${getAvatarColor(user?.email)} flex items-center justify-center text-white font-medium text-sm shadow-sm`}>
                    {user?.email ? (
                      getUserInitials(user.email)
                    ) : (
                      <User className="h-5 w-5" />
                    )}
                  </div>
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div
                    ref={menuRef}
                    className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
                    role="menu"
                  >
                    {installPrompt && (
                      <button
                        onClick={handleInstallApp}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                      >
                        <Download className="h-4 w-4 mr-3" />
                        Add to Home Screen
                      </button>
                    )}
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Settings
                    </Link>
                    <button
                      onClick={handleUpdatePassword}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <Lock className="h-4 w-4 mr-3" />
                      Update Password
                    </button>
                    <Link
                      to="/help"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <HelpCircle className="h-4 w-4 mr-3" />
                      Help
                    </Link>
                    <button
                      onClick={handleSendFeedback}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <MessageSquare className="h-4 w-4 mr-3" />
                      Send Feedback
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Beta banner */}
      {user?.is_active && !betaBannerDismissed && (
        <div className="border-b bg-indigo-50 border-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <p className="text-sm text-indigo-700">
              <span className="font-semibold">VocaBranch is in beta</span> — it's completely free while we're testing. Thanks for helping us shape it!
              <button
                onClick={() => setShowFeedback(true)}
                className="ml-3 underline underline-offset-2 hover:text-indigo-900 transition-colors font-medium"
              >
                Share feedback
              </button>
            </p>
            <button
              onClick={dismissBetaBanner}
              className="flex-shrink-0 p-1 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Setup error banner (e.g. auto-create workspace/instance failed) */}
      {setupError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
            <div className="flex items-center gap-2 min-w-0">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm font-medium">{setupError}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                clearSetupError();
                refreshInstances();
              }}
              className="flex items-center gap-1.5 flex-shrink-0 rounded-md bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-200 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>

      {/* Update Password Modal */}
      {showUpdatePassword && (
        <UpdatePassword onClose={() => setShowUpdatePassword(false)} />
      )}

      {/* Feedback Modal */}
      {showFeedback && (
        <FeedbackModal onClose={() => setShowFeedback(false)} />
      )}
    </div>
  );
};

